import {
  triggerEventOptions,
  triggerGroupUiOptions,
  getEventUiGroup,
} from './trigger-event-options';
import { useMemo, useState } from 'react';
import { TriggerInfo } from '../../../../types';
import PanelSectionHeader, {
  SummaryPanelVariant,
} from '../../../shared/panel-section-header';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Bolt from '@mui/icons-material/Bolt';

interface RulesSummaryWhenSectionProps {
  triggerInfo?: TriggerInfo;
  triggerSources?: string[];
  allSource?: boolean;
  variant?: SummaryPanelVariant;
}

export default function RulesSummaryWhenSection({
  triggerInfo,
  triggerSources,
  allSource,
  variant = 'full',
}: RulesSummaryWhenSectionProps) {
  const theme = useTheme();
  const resolvedTriggerGroup =
    triggerInfo?.triggerEvent &&
    getEventUiGroup(triggerInfo.triggerEvent) === 'groupEvents'
      ? 'groupEvents'
      : triggerInfo?.triggerGroup;

  const triggerGroup = triggerGroupUiOptions.find(
    (group) => group.value === resolvedTriggerGroup
  )?.displayValue;
  const triggerEvent = triggerEventOptions.find(
    (group) =>
      group.value === triggerInfo?.triggerEvent &&
      group.type === triggerInfo?.triggerGroup
  )?.displayValue;
  const occursEverytime = triggerInfo?.occursEverytime;
  const triggerEverytime = occursEverytime ? 'Every Time' : 'Threshold';
  const isAllSources = allSource;
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const triggerEventSummary =
    triggerGroup && triggerEvent ? `${triggerGroup} : ${triggerEvent}` : '—';
  const isCompact = variant === 'compact';
  const mainFontVariant = isCompact ? 'caption' : 'body2';

  const sourceSummary = useMemo(() => {
    if (isAllSources) return 'All Sources';
    const sources = triggerSources || [];

    if (!sources || sources.length < 1) {
      return '—';
    }

    const maxLength = isCompact ? 40 : 50;
    const sourceTruncateLength = isCompact ? 35 : 45;

    const truncateSource = (source: string | undefined) => {
      if (!source) return;
      if (source.length >= sourceTruncateLength) {
        return `${source.slice(0, sourceTruncateLength - 3)}...`;
      }

      return source;
    };

    if (sourcesExpanded) {
      return (
        <>
          {sources.map((source, index) => (
            <span key={source}>
              {index > 0 && ', '}
              {source}
            </span>
          ))}

          {sources.length > 1 && (
            <>
              {', '}
              <Box
                component="span"
                onClick={() => setSourcesExpanded(false)}
                sx={{
                  color: theme.palette.semantic.hyperlink.active,
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Less
              </Box>
            </>
          )}
        </>
      );
    }

    let summary = '';

    for (let i = 0; i < sources.length; i++) {
      const remaining = sources.length - i - 1;
      const suffix = remaining > 0 ? `, +${remaining}` : '';
      const separator = summary ? ', ' : '';
      const displaySource = truncateSource(sources[i]);

      const candidate = `${summary}${separator}${displaySource}${suffix}`;

      if (candidate.length > maxLength) {
        const hiddenCount = sources.length - i;

        return (
          <>
            {summary && `${summary}, `}
            <Box
              component="span"
              onClick={() => setSourcesExpanded(true)}
              sx={{
                color: theme.palette.semantic.hyperlink.active,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              +{hiddenCount}
            </Box>
          </>
        );
      }

      summary = `${summary}${separator}${displaySource}`;
    }

    return summary;
  }, [isAllSources, triggerSources, isCompact, sourcesExpanded, theme]);

  return (
    <Box
      sx={{
        mt: 2,
        spacing: 1,
      }}
    >
      <PanelSectionHeader variant={variant} title="WHEN" icon={Bolt} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          px: 1,
          ...(!isCompact && {
            backgroundColor: theme.palette.semantic.surface.bg3,
            p: 2,
            border: '1px solid',
            borderColor: theme.palette.semantic.outline.light,
          }),
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={3}
          >
            <Typography
              variant={mainFontVariant}
              sx={{
                minWidth: 20,
                lineHeight: 1.2,
                color: theme.palette.semantic.text.title2,
              }}
            >
              Event
            </Typography>

            <Typography
              variant={mainFontVariant}
              sx={{
                flex: 1,
                textAlign: 'right',
                lineHeight: 1.2,
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: theme.palette.semantic.text.title2,
              }}
            >
              {triggerEventSummary}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={3}
          >
            <Typography
              variant={mainFontVariant}
              sx={{
                minWidth: 20,
                lineHeight: 1.2,
                color: theme.palette.semantic.text.title2,
              }}
            >
              Occurences
            </Typography>

            <Typography
              variant={mainFontVariant}
              sx={{
                flex: 1,
                textAlign: 'right',
                lineHeight: 1.2,
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: theme.palette.semantic.text.title2,
              }}
            >
              {triggerEverytime}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={3}
            sx={{ minWidth: 0 }}
          >
            <Typography
              variant={mainFontVariant}
              sx={{
                minWidth: 20,
                flexShrink: 0,
                lineHeight: 1.2,
                color: theme.palette.semantic.text.title2,
              }}
            >
              Sources
            </Typography>

            <Typography
              variant={mainFontVariant}
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: '80%',
                textAlign: 'right',
                lineHeight: 1.2,
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: theme.palette.semantic.text.title2,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {sourceSummary}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
